import type {
  AttributesEntity,
  ValuesEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import { AttributeCheckbox } from '@eliadmin/core/src/components/categories/attributes/attribute-checkbox';
import { AttributeColor } from '@eliadmin/core/src/components/categories/attributes/attribute-color';
import {
  AttributeGeneric,
  type GenericType,
} from '@eliadmin/core/src/components/categories/attributes/attribute-generic';
import { AttributePrice } from '@eliadmin/core/src/components/categories/attributes/attribute-price';
import { AttributeRadio } from '@eliadmin/core/src/components/categories/attributes/attribute-radio';
import { AttributeSelect } from '@eliadmin/core/src/components/categories/attributes/attribute-select';
import { AttributeSlider } from '@eliadmin/core/src/components/categories/attributes/attribute-slider';
import { AttributeSubCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-subcategory';
import { AttributeSupplierLocation } from '@eliadmin/core/src/components/categories/attributes/attribute-supplier-location';
import { Card } from '@eliadmin/core/src/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { AttributeType, useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuMinus, LuPlus } from 'react-icons/lu';

export function AttributeSide({
  type,
  name,
  attribute,
  collapsible = true,
  className,
}: {
  type: 'subCategory' | 'generic' | 'price' | 'supplierLocation';
  name: string;
  attribute: GenericType;
  collapsible?: boolean;
  order?: number;
  className?: {
    wrapper?: string;
    trigger?: string;
  };
}) {
  const { isOpen, setIsOpen } = useDisclosure(false);

  return (
    <Card className={cn('w-full bg-transparent', 'ea-attribute-side-card', className?.wrapper)}>
      <Collapsible
        className={cn('flex w-full flex-col space-y-4', 'ea-attribute-side-card__collapsible')}
        open={isOpen}
        onOpenChange={collapsible ? setIsOpen : undefined}
      >
        <CollapsibleTrigger
          className={cn(
            'flex w-full flex-row items-center justify-between gap-2',
            'ea-attribute-side-card__collapsible-trigger',
            className?.trigger,
            { 'pointer-events-none': !collapsible },
          )}
        >
          <span className={cn('h6', ' font-semibold tracking-tight')}>{name}</span>
          {collapsible && (
            <span className={cn('text-primary')}>
              {isOpen ? (
                <LuMinus size={20} className="text-primary" />
              ) : (
                <LuPlus size={20} className="text-primary" />
              )}
            </span>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(
            'm-0 w-full',
            'ea-attribute-side-card__collapsible-content',
            type === 'supplierLocation' && 'px-0',
          )}
        >
          {type === 'subCategory' && <AttributeSubCategory />}
          {type === 'generic' && <AttributeGeneric type={attribute.type} type2={attribute.type2} />}
          {type === 'price' && <AttributePrice />}
          {type === 'supplierLocation' && <AttributeSupplierLocation />}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export function AttributeSideDynamic({
  name,
  attribute,
  collapsible = true,
  order = 0,
  className,
}: {
  name: string;
  attribute: AttributesEntity;
  collapsible?: boolean;
  order?: number;
  className?: {
    wrapper?: string;
    trigger?: string;
  };
}) {
  // Large Mediatip categories can contain thousands of filter values. Keep
  // large groups collapsed until the customer explicitly asks for them while
  // preserving the configured default for small groups.
  const startsOpen = Boolean(attribute.collapse) && (attribute.values?.length ?? 0) <= 12;
  const { isOpen, setIsOpen } = useDisclosure(collapsible ? startsOpen : false);
  // const updateSelectedValues = useVariantsStore(state => state.updateSelectedValue);
  // const show = collapsible ? isOpen : true;
  const filter = useCategoryStore(state => state.filter.attributes);

  const updateVariantValues = (attribute: AttributesEntity, value: ValuesEntity) => {
    const filterAttribute = filter.find(item => item.id === attribute.id);

    if (filterAttribute) {
      if (filterAttribute.values.includes(value.id)) {
        // updateSelectedValues({ attribute, value });
      } else if (filterAttribute.values.length > 0) {
        // if (val) {
        // updateSelectedValues({ attribute: attribute, value: val });
        // }
      }
    }
  };

  return (
    <Card className={cn('rounded-md bg-transparent', className?.wrapper)}>
      <Collapsible
        className={cn(
          `flex w-full flex-col gap-4 order-${order}`,
          'ea-attribute-side-dynamic__collapsible',
        )}
        open={collapsible ? isOpen : true}
        onOpenChange={setIsOpen}
      >
        <CollapsibleTrigger
          className={cn(
            'flex w-full flex-row items-center justify-between gap-2',
            'ea-attribute-side-dynamic__collapsible-trigger',
            className?.trigger,
            { 'pointer-events-none': !collapsible },
          )}
        >
          <span className={cn('h6', 'font-semibold tracking-tight text-black')}>{name}</span>
          {collapsible &&
            (isOpen ? (
              <LuMinus size={20} className="text-primary" />
            ) : (
              <LuPlus size={20} className="text-primary" />
            ))}
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn('m-0 w-full', 'ea-attribute-side-dynamic__collapsible-content')}
        >
          {attribute.type === AttributeType.CHECKBOX && (
            <AttributeCheckbox
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__checkbox')}
            />
          )}
          {attribute.type === AttributeType.SIZE && (
            <AttributeCheckbox
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__size')}
            />
          )}
          {attribute.type === AttributeType.RADIO && (
            <AttributeRadio
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__radio')}
            />
          )}
          {attribute.type === AttributeType.COLOR && (
            <AttributeColor
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__color')}
            />
          )}
          {attribute.type === AttributeType.SELECT && (
            <AttributeSelect
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__select')}
            />
          )}
          {(attribute.type === AttributeType.SLIDER_RADIO ||
            attribute.type === AttributeType.SLIDER_SELECT) && (
            <AttributeSlider
              attribute={attribute}
              updateVariantValues={updateVariantValues}
              className={cn('', 'ea-attribute-side-dynamic__slider')}
            />
          )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
